import React, { useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Avatar,
  LinearProgress,
  Stack,
  Tooltip,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  EmojiEvents,
  WorkspacePremium,
  Star,
  TrendingUp,
  InfoOutlined,
  GppGood,
  Inventory2,
  ContentCut,
  Close,
  BarChartRounded,
} from "@mui/icons-material";
import StaffPerformanceDetails from "./StaffPerformanceDetails";

function stringToColor(string) {
  if (!string) return "#000000";
  let hash = 0;
  for (let i = 0; i < string.length; i += 1) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
  }
  let color = "#";
  for (let i = 0; i < 3; i += 1) {
    const value = (hash >> (i * 8)) & 0xff;
    color += `00${value.toString(16)}`.slice(-2);
  }
  return color;
}

const fmt = (n) =>
  `₹${(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const StaffPerformanceMonthlyView = ({ data, selectedMonth, ownerId }) => {
  const theme = useTheme();
  const [selectedStaffForModal, setSelectedStaffForModal] = useState(null);

  const sortedData = [...data].sort((a, b) => {
    const aAmount = a.monthly?.find((m) => m.month === selectedMonth)?.combinedServiceDueAmount || 0;
    const bAmount = b.monthly?.find((m) => m.month === selectedMonth)?.combinedServiceDueAmount || 0;
    return bAmount - aAmount;
  });

  const maxWorkAmount =
    sortedData[0]?.monthly?.find((m) => m.month === selectedMonth)?.combinedServiceDueAmount || 1;

  const renderStaffCard = (staff, index) => {
    const monthData = staff.monthly.find((m) => m.month === selectedMonth);
    if (!monthData) return null;

    const totalSales = monthData.combinedServiceDueAmount ?? monthData.totalWorkAmount ?? 0;
    const relativePerformance = (totalSales / maxWorkAmount) * 100;
    const isApex = index === 0;

    // Target is a yearly figure in the schema; comparing against this month's
    // sales gives a rough monthly pace toward it.
    const target = Number(staff.target || 0);
    const targetProgress = target > 0 ? Math.min((totalSales / target) * 100, 100) : 0;

    let borderColor = "transparent";
    let bgColor = theme.palette.background.paper;
    let Icon = TrendingUp;

    if (isApex) {
      borderColor = "#FFD700";
      bgColor = "#FFFDF0";
      Icon = EmojiEvents;
    } else if (index === 1) {
      borderColor = "#C0C0C0";
      Icon = WorkspacePremium;
    } else if (index === 2) {
      borderColor = "#CD7F32";
      Icon = Star;
    }

    return (
      <Card
        sx={{
          borderRadius: 4,
          boxShadow: isApex ? "0 10px 30px rgba(255, 215, 0, 0.2)" : 3,
          border: `2px solid ${borderColor}`,
          bgcolor: bgColor,
          position: "relative",
          transition: "transform 0.2s",
          "&:hover": { transform: "translateY(-5px)" },
        }}
      >
        <CardContent>
          {/* HEADER */}
          <Box sx={{ display: "flex", alignItems: "center", mb: 1.5, justifyContent: "space-between" }}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Box sx={{ position: "relative", mr: 2 }}>
                <Avatar
                  sx={{
                    bgcolor: stringToColor(staff.staffName),
                    width: isApex ? 56 : 40,
                    height: isApex ? 56 : 40,
                  }}
                >
                  {staff.staffName?.charAt(0).toUpperCase()}
                </Avatar>
                <Box
                  sx={{
                    position: "absolute",
                    bottom: -5,
                    right: -5,
                    bgcolor: borderColor !== "transparent" ? borderColor : "grey.400",
                    borderRadius: "50%",
                    width: 22,
                    height: 22,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "2px solid white",
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: "bold", color: "white", fontSize: "10px" }}>
                    {index + 1}
                  </Typography>
                </Box>
              </Box>
              <Box>
                <Typography variant={isApex ? "h6" : "subtitle1"} fontWeight="800">
                  {staff.staffName}
                </Typography>
                <Typography variant="caption" color="text.secondary" fontWeight="600">
                  {isApex ? "Performance Leader" : "Team Member"}
                </Typography>
              </Box>
            </Box>
            <Icon
              sx={{
                color: borderColor !== "transparent" ? borderColor : "disabled.main",
                fontSize: isApex ? "2.2rem" : "1.4rem",
              }}
            />
          </Box>

          {/* INCENTIVE TYPE BADGES */}
          <Stack direction="row" spacing={1} sx={{ mb: 1.5 }} flexWrap="wrap" useFlexGap>
            <Tooltip arrow title="This staff has earned incentive on at least one service bill this year">
              <Chip
                size="small"
                icon={<ContentCut sx={{ fontSize: "0.9rem !important" }} />}
                label={staff.isServiceIncludeInIncentive ? "Service Incentive: On" : "Service Incentive: Off"}
                sx={{
                  bgcolor: staff.isServiceIncludeInIncentive ? theme.palette.success.light : theme.palette.grey[200],
                  color: staff.isServiceIncludeInIncentive ? theme.palette.success.dark : theme.palette.text.secondary,
                  fontWeight: 600,
                }}
              />
            </Tooltip>
            <Tooltip arrow title="This staff has earned incentive on at least one product bill this year">
              <Chip
                size="small"
                icon={<Inventory2 sx={{ fontSize: "0.9rem !important" }} />}
                label={staff.isProductIncludeInIncentive ? "Product Incentive: On" : "Product Incentive: Off"}
                sx={{
                  bgcolor: staff.isProductIncludeInIncentive ? theme.palette.warning.light : theme.palette.grey[200],
                  color: staff.isProductIncludeInIncentive ? theme.palette.warning.dark : theme.palette.text.secondary,
                  fontWeight: 600,
                }}
              />
            </Tooltip>
          </Stack>

          {/* VS TOP PERFORMER */}
          <Box sx={{ mb: 1.5 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
              <Typography variant="caption" fontWeight="700" color="text.secondary">
                VS TOP PERFORMER
              </Typography>
              <Typography variant="caption" fontWeight="800" color="primary">
                {relativePerformance.toFixed(0)}%
              </Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={relativePerformance}
              sx={{
                height: 8,
                borderRadius: 5,
                bgcolor: theme.palette.grey[200],
                "& .MuiLinearProgress-bar": { borderRadius: 5 },
              }}
            />
          </Box>

          {/* TARGET — static tag */}
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <GppGood sx={{ fontSize: "0.9rem", color: theme.palette.info.main }} />
                <Typography variant="caption" fontWeight="700" color="text.secondary">
                  TARGET
                </Typography>
              </Stack>
              <Chip
                size="small"
                label={target > 0 ? fmt(target) : "Not set"}
                sx={{
                  fontWeight: 800,
                  bgcolor: target > 0 ? theme.palette.info.light : theme.palette.grey[200],
                  color: target > 0 ? theme.palette.info.dark : theme.palette.text.secondary,
                }}
              />
            </Box>
          {/* QUICK STATS */}
          <Grid container spacing={2} sx={{ mb: 1.5 }}>
            <Grid item xs={4}>
              <Typography variant="caption" color="text.secondary" display="block">Service</Typography>
              <Typography variant="body2" fontWeight="700">{fmt(monthData.totalServiceAmount)}</Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="caption" color="text.secondary" display="block">Product</Typography>
              <Typography variant="body2" fontWeight="700">{fmt(monthData.totalProductAmount)}</Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="caption" color="text.secondary" display="block">Incentive</Typography>
              <Typography variant="body2" fontWeight="700" color="success.main">
                {fmt(monthData.monthlyIncentiveAmount)}
              </Typography>
            </Grid>
          </Grid>

          {/* TOTAL SALES BOX */}
          <Box
            sx={{
              p: 1.5,
              bgcolor: isApex ? "primary.main" : "primary.light",
              color: isApex ? "primary.contrastText" : "primary.main",
              borderRadius: 2,
              textAlign: "center",
              mb: 1.5,
            }}
          >
            <Stack direction="row" spacing={0.5} justifyContent="center" alignItems="center">
              <Typography variant="caption" sx={{ textTransform: "uppercase", fontWeight: "800", opacity: 0.85 }}>
                Total Month Sales
              </Typography>
              <Tooltip arrow title="Total Sales = Service + Product billed this month + backdated bill entries created this month">
                <InfoOutlined sx={{ fontSize: "0.9rem", opacity: 0.85, cursor: "help" }} />
              </Tooltip>
            </Stack>
            <Typography variant="h5" fontWeight="900">{fmt(totalSales)}</Typography>
          </Box>

          {/* VIEW DETAILS BUTTON */}
          <Button
            fullWidth
            variant="outlined"
            startIcon={<BarChartRounded />}
            onClick={() => setSelectedStaffForModal(staff)}
            sx={{ borderRadius: 2, fontWeight: 700 }}
          >
            View Details
          </Button>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box>
      {/* APEX LEVEL */}
      <Grid container spacing={3} justifyContent="center" sx={{ mb: 4 }}>
        {sortedData[0] && (
          <Grid item xs={12} md={8} lg={6}>
            {renderStaffCard(sortedData[0], 0)}
          </Grid>
        )}
      </Grid>

      {/* PODIUM LEVEL */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {sortedData.slice(1, 3).map((staff, i) => (
          <Grid item xs={12} md={6} key={staff.staffId}>
            {renderStaffCard(staff, i + 1)}
          </Grid>
        ))}
      </Grid>

      {/* REMAINING STAFF */}
      <Grid container spacing={3}>
        {sortedData.slice(3).map((staff, i) => (
          <Grid item xs={12} sm={6} md={4} key={staff.staffId}>
            {renderStaffCard(staff, i + 3)}
          </Grid>
        ))}
      </Grid>

      {/* DETAILED PERFORMANCE DIALOG */}
      <Dialog
        open={Boolean(selectedStaffForModal)}
        onClose={() => setSelectedStaffForModal(null)}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: { height: "92vh", maxHeight: "92vh", borderRadius: 3 },
        }}
      >
        <DialogTitle
          sx={{ m: 0, p: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}
        >
          <Typography variant="h6">
            {selectedStaffForModal?.staffName} — Detailed Performance
          </Typography>
          <IconButton onClick={() => setSelectedStaffForModal(null)} size="small">
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3, overflowY: "auto" }}>
          {selectedStaffForModal && (
            <StaffPerformanceDetails
              staffData={selectedStaffForModal}
              selectedMonth={selectedMonth}
              ownerId={ownerId}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default StaffPerformanceMonthlyView;